import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Shield,
  Home,
  FileText,
  Palette,
  Users,
  CreditCard,
  LogOut,
  CheckCircle,
  Circle,
  Clock,
  Download,
  Send,
  DollarSign,
  Gift,
  User,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Star,
  ExternalLink,
  Copy,
  Check,
  MessageSquare,
  Paperclip
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getCustomerHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('customer_token')}`
});

// Project phase configuration
const PHASES = [
  { id: 'contract_signed', label: 'Contract Signed', icon: FileText },
  { id: 'color_selection', label: 'Color Selection', icon: Palette },
  { id: 'scheduling', label: 'Scheduling', icon: Clock },
  { id: 'in_production', label: 'In Production', icon: Shield },
  { id: 'quality_check', label: 'Quality Check', icon: CheckCircle },
  { id: 'completed', label: 'Completed', icon: Home }
];

// Review platforms configuration
const REVIEW_PLATFORMS = [
  {
    id: 'google',
    name: 'Google',
    icon: '🔍',
    url: 'https://g.page/r/CeKGiNF1ICoqEBM/review',
    color: 'bg-blue-500',
    description: 'Most impactful for local search'
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: '📘',
    url: 'https://www.facebook.com/RhinoShieldJax/reviews',
    color: 'bg-[#1877f2]',
    description: 'Reach friends and family'
  },
  {
    id: 'yelp',
    name: 'Yelp',
    icon: '⭐',
    url: 'https://www.yelp.com/biz/rhino-shield-jacksonville',
    color: 'bg-red-500',
    description: 'Trusted by homeowners'
  },
  {
    id: 'bbb',
    name: 'BBB',
    icon: '🏆',
    url: 'https://www.bbb.org/us/fl/jacksonville/profile/painting-contractors/rhino-shield-0403-90194621',
    color: 'bg-[#00529b]',
    description: 'Better Business Bureau'
  }
];

export default function CustomerPortal() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [projectStatus, setProjectStatus] = useState(null);
  const [colorSelection, setColorSelection] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], has_reviewed: false });
  
  // Dialog states
  const [showColorDialog, setShowColorDialog] = useState(false);
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  
  // Form states
  const [colorForm, setColorForm] = useState({
    primary_color: '',
    accent_color: '',
    trim_color: '',
    notes: ''
  });
  const [referralForm, setReferralForm] = useState({
    referee_name: '',
    referee_email: '',
    referee_phone: '',
    referee_address: '',
    notes: ''
  });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    review_text: '',
    would_recommend: true,
    allow_public: true,
    platforms: ['google']
  });
  const [reviewStep, setReviewStep] = useState(1); // 1: Write review, 2: Select platforms, 3: Post to platforms
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Project Team state
  const [projectTeam, setProjectTeam] = useState({ team: [], has_assigned_team: false, default_contact: {} });
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [showTeamMessageDialog, setShowTeamMessageDialog] = useState(false);
  const [teamMessage, setTeamMessage] = useState('');
  const [activeConversation, setActiveConversation] = useState('all'); // 'all' or team member id

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    if (!token) {
      navigate('/customer-login');
    }
  }, [navigate]);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, docsRes, statusRes, colorsRes, referralsRes, paymentsRes, reviewsRes, teamRes] = await Promise.all([
        axios.get(`${API}/api/customer-portal/dashboard`, { headers: getCustomerHeaders() }),
        axios.get(`${API}/api/customer-portal/documents`, { headers: getCustomerHeaders() }),
        axios.get(`${API}/api/customer-portal/project-status`, { headers: getCustomerHeaders() }),
        axios.get(`${API}/api/customer-portal/colors`, { headers: getCustomerHeaders() }),
        axios.get(`${API}/api/customer-portal/referrals`, { headers: getCustomerHeaders() }),
        axios.get(`${API}/api/customer-portal/payments`, { headers: getCustomerHeaders() }),
        axios.get(`${API}/api/customer-portal/reviews`, { headers: getCustomerHeaders() }),
        axios.get(`${API}/api/customer-portal/project-team`, { headers: getCustomerHeaders() })
      ]);
      
      setDashboard(dashboardRes.data);
      setDocuments(docsRes.data.documents || []);
      setProjectStatus(statusRes.data);
      setColorSelection(colorsRes.data);
      setReferrals(referralsRes.data.referrals || []);
      setPayments(paymentsRes.data);
      setReviews(reviewsRes.data);
      setProjectTeam(teamRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('customer_token');
        localStorage.removeItem('customer_data');
        navigate('/customer-login');
      }
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const response = await axios.get(`${API}/api/customer-portal/messages`, {
        headers: getCustomerHeaders()
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // Fetch messages when messages tab is active
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchMessages();
    }
  }, [activeTab, fetchMessages]);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      // If a specific team member is selected, use the team message endpoint
      if (activeConversation !== 'all') {
        await axios.post(`${API}/api/customer-portal/project-team/message`, {
          content: newMessage.trim(),
          team_member_id: activeConversation
        }, { headers: getCustomerHeaders() });
      } else {
        await axios.post(`${API}/api/customer-portal/messages`, {
          content: newMessage.trim()
        }, { headers: getCustomerHeaders() });
      }
      
      setNewMessage('');
      fetchMessages();
      toast.success('Message sent!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Get filtered messages for active conversation
  const getFilteredMessages = () => {
    if (activeConversation === 'all') {
      return messages;
    }
    return messages.filter(msg => 
      msg.recipient_id === activeConversation || 
      (msg.is_from_customer && msg.recipient_id === activeConversation) ||
      (!msg.is_from_customer && msg.sender_id === activeConversation)
    );
  };

  // Get conversation preview for a team member
  const getLastMessage = (memberId) => {
    const memberMessages = messages.filter(msg => 
      msg.recipient_id === memberId || 
      (!msg.is_from_customer && msg.sender_id === memberId)
    );
    return memberMessages[memberMessages.length - 1];
  };

  // Get unread count for a team member
  const getUnreadCount = (memberId) => {
    if (memberId === 'all') {
      return messages.filter(msg => !msg.is_from_customer && !msg.read).length;
    }
    return messages.filter(msg => 
      !msg.is_from_customer && 
      !msg.read && 
      msg.sender_id === memberId
    ).length;
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    toast.success('Logged out successfully');
    navigate('/customer-login');
  };

  // Send message to team member
  const handleTeamMessage = async () => {
    if (!teamMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/api/customer-portal/project-team/message`, {
        content: teamMessage.trim(),
        team_member_id: selectedTeamMember?.user?.id || 'all'
      }, { headers: getCustomerHeaders() });
      
      const recipientName = selectedTeamMember?.user 
        ? `${selectedTeamMember.user.first_name} ${selectedTeamMember.user.last_name}`
        : 'the team';
      toast.success(`Message sent to ${recipientName}!`);
      setShowTeamMessageDialog(false);
      setTeamMessage('');
      setSelectedTeamMember(null);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Open message dialog for team member
  const openTeamMessageDialog = (member = null) => {
    setSelectedTeamMember(member);
    setTeamMessage('');
    setShowTeamMessageDialog(true);
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase() || '?';
  };

  // Get role icon color
  const getRoleColor = (role) => {
    const colors = {
      sales_rep: 'from-blue-500 to-blue-600',
      project_manager: 'from-purple-500 to-purple-600',
      operations: 'from-green-500 to-green-600',
      support: 'from-amber-500 to-amber-600'
    };
    return colors[role] || 'from-gray-500 to-gray-600';
  };

  // Submit color confirmation
  const handleColorSubmit = async () => {
    try {
      await axios.post(`${API}/api/customer-portal/colors/confirm`, colorForm, {
        headers: getCustomerHeaders()
      });
      toast.success('Color selection confirmed!');
      setShowColorDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to confirm colors');
    }
  };

  // Submit referral
  const handleReferralSubmit = async () => {
    if (!referralForm.referee_name || !referralForm.referee_phone) {
      toast.error('Please fill in name and phone number');
      return;
    }
    try {
      await axios.post(`${API}/api/customer-portal/referrals`, referralForm, {
        headers: getCustomerHeaders()
      });
      toast.success('Referral submitted! Thank you!');
      setShowReferralDialog(false);
      setReferralForm({ referee_name: '', referee_email: '', referee_phone: '', referee_address: '', notes: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to submit referral');
    }
  };

  // Create payment intent
  const handlePayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await axios.post(`${API}/api/customer-portal/payments/create-intent`, {
        amount,
        description: 'Portal payment'
      }, { headers: getCustomerHeaders() });
      
      toast.success('Payment system ready. Stripe integration required for production.');
      setShowPaymentDialog(false);
      setPaymentAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    }
  };

  // Submit review to internal system
  const handleReviewSubmit = async () => {
    if (!reviewForm.review_text.trim()) {
      toast.error('Please write your review');
      return;
    }
    try {
      await axios.post(`${API}/api/customer-portal/reviews`, {
        ...reviewForm,
        platforms: reviewForm.platforms
      }, {
        headers: getCustomerHeaders()
      });
      toast.success('Review saved! Now let\'s share it on other platforms.');
      setReviewStep(3); // Move to platform posting step
      fetchAllData();
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  // Copy review to clipboard
  const copyReviewToClipboard = () => {
    const reviewText = `${reviewForm.rating}/5 Stars\n\n${reviewForm.title ? reviewForm.title + '\n\n' : ''}${reviewForm.review_text}`;
    navigator.clipboard.writeText(reviewText);
    setCopiedToClipboard(true);
    toast.success('Review copied to clipboard!');
    setTimeout(() => setCopiedToClipboard(false), 3000);
  };

  // Open platform for review
  const openPlatformForReview = (platform) => {
    copyReviewToClipboard();
    window.open(platform.url, '_blank');
  };

  // Toggle platform selection
  const togglePlatform = (platformId) => {
    setReviewForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  // Star rating component
  const StarRating = ({ rating, onRatingChange, readonly = false }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => !readonly && onRatingChange(star)}
            disabled={readonly}
            className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
          >
            <Star
              className={`h-8 w-8 ${
                star <= rating
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'fill-none text-white/30'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  // Reset review dialog
  const resetReviewDialog = () => {
    setShowReviewDialog(false);
    setReviewStep(1);
    setReviewForm({ rating: 5, title: '', review_text: '', would_recommend: true, allow_public: true, platforms: ['google'] });
    setCopiedToClipboard(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1f2e] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2dd4bf]/30 border-t-[#2dd4bf] rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-white/60">Loading your portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f2e]">
      {/* Header */}
      <header className="bg-[#0f1219] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#2dd4bf] to-[#0d9488] rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/20">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-white">Customer Portal</h1>
                <p className="text-xs text-[#2dd4bf]">Rhino Shield Florida</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-white">{dashboard?.customer?.name}</p>
                <p className="text-xs text-white/50">{dashboard?.customer?.email}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-white/70 hover:text-white hover:bg-white/10"
                data-testid="customer-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 bg-[#0f1219] p-1 border border-white/10">
            <TabsTrigger value="dashboard" className="gap-2 data-[state=active]:bg-[#2dd4bf] data-[state=active]:text-white text-white/60">
              <Home className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2 data-[state=active]:bg-[#2dd4bf] data-[state=active]:text-white text-white/60">
              <MessageSquare className="h-4 w-4" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2 data-[state=active]:bg-[#2dd4bf] data-[state=active]:text-white text-white/60">
              <FileText className="h-4 w-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="colors" className="gap-2 data-[state=active]:bg-[#2dd4bf] data-[state=active]:text-white text-white/60">
              <Palette className="h-4 w-4" />
              Colors
            </TabsTrigger>
            <TabsTrigger value="referrals" className="gap-2 data-[state=active]:bg-[#2dd4bf] data-[state=active]:text-white text-white/60">
              <Users className="h-4 w-4" />
              Referrals
            </TabsTrigger>
            <TabsTrigger value="payments" className="gap-2 data-[state=active]:bg-[#2dd4bf] data-[state=active]:text-white text-white/60">
              <CreditCard className="h-4 w-4" />
              Payments
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6" data-testid="customer-dashboard">
            {/* Welcome Banner */}
            <Card className="bg-gradient-to-r from-[#0d9488] to-[#2dd4bf] border-0 overflow-hidden relative">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-white/20" />
                <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-white/10" />
              </div>
              <CardContent className="p-6 relative">
                <h2 className="text-2xl font-bold mb-2 text-white">Welcome, {dashboard?.customer?.name?.split(' ')[0]}!</h2>
                <p className="text-white/80">Track your project progress, view documents, and manage your account.</p>
              </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-[#0f1219] border-white/10">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-[#2dd4bf]">{dashboard?.project?.progress || 0}%</div>
                  <p className="text-sm text-white/50 mt-1">Project Progress</p>
                </CardContent>
              </Card>
              <Card className="bg-[#0f1219] border-white/10">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-green-400">{dashboard?.counts?.documents || 0}</div>
                  <p className="text-sm text-white/50 mt-1">Documents</p>
                </CardContent>
              </Card>
              <Card className="bg-[#0f1219] border-white/10">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-purple-400">{dashboard?.counts?.referrals || 0}</div>
                  <p className="text-sm text-white/50 mt-1">Referrals</p>
                </CardContent>
              </Card>
              <Card className="bg-[#0f1219] border-white/10">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-amber-400">
                    ${(dashboard?.financials?.balance_due || 0).toLocaleString()}
                  </div>
                  <p className="text-sm text-white/50 mt-1">Balance Due</p>
                </CardContent>
              </Card>
            </div>

            {/* Project Progress */}
            <Card className="bg-[#0f1219] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Project Progress</CardTitle>
                <CardDescription className="text-white/50">Your project status and timeline</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {/* Progress bar */}
                  <div className="absolute top-5 left-0 right-0 h-1 bg-white/10 rounded">
                    <div 
                      className="h-full bg-gradient-to-r from-[#2dd4bf] to-[#0d9488] rounded transition-all duration-500"
                      style={{ width: `${dashboard?.project?.progress || 0}%` }}
                    />
                  </div>
                  
                  {/* Phase steps */}
                  <div className="flex justify-between relative">
                    {projectStatus?.phases?.map((phase, idx) => {
                      const PhaseIcon = PHASES.find(p => p.id === phase.id)?.icon || Circle;
                      return (
                        <div key={phase.id} className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all ${
                            phase.completed ? 'bg-[#2dd4bf] text-white shadow-lg shadow-teal-500/30' :
                            phase.current ? 'bg-[#2dd4bf] text-white ring-4 ring-[#2dd4bf]/30' :
                            'bg-white/10 text-white/40'
                          }`}>
                            {phase.completed ? <CheckCircle className="h-5 w-5" /> : <PhaseIcon className="h-5 w-5" />}
                          </div>
                          <span className={`text-xs mt-2 text-center max-w-[80px] ${
                            phase.current ? 'font-semibold text-[#2dd4bf]' : 'text-white/50'
                          }`}>
                            {phase.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Your Project Team */}
            <Card className="bg-[#0f1219] border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="h-5 w-5 text-[#2dd4bf]" />
                      Your Project Team
                    </CardTitle>
                    <CardDescription className="text-white/50">
                      Contact your dedicated team members directly
                    </CardDescription>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => openTeamMessageDialog(null)}
                    className="bg-[#2dd4bf] hover:bg-[#14b8a6] text-white"
                    data-testid="message-entire-team-btn"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Message Entire Team
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4">
                  {projectTeam.team.map((member) => (
                    <div 
                      key={member.role}
                      className="relative p-4 bg-white/5 rounded-xl border border-white/10 hover:border-[#2dd4bf]/30 transition-all group"
                    >
                      {/* Profile Avatar */}
                      <div className="flex flex-col items-center text-center">
                        {member.assigned && member.user?.profile_image ? (
                          <img 
                            src={member.user.profile_image} 
                            alt={`${member.user.first_name} ${member.user.last_name}`}
                            className="w-16 h-16 rounded-full object-cover border-2 border-white/20 mb-3"
                          />
                        ) : (
                          <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getRoleColor(member.role)} flex items-center justify-center mb-3 shadow-lg`}>
                            {member.assigned ? (
                              <span className="text-white text-lg font-bold">
                                {getInitials(member.user?.first_name, member.user?.last_name)}
                              </span>
                            ) : (
                              <User className="h-7 w-7 text-white/70" />
                            )}
                          </div>
                        )}
                        
                        {/* Name & Title */}
                        <h4 className="font-semibold text-white text-sm">
                          {member.assigned ? `${member.user.first_name} ${member.user.last_name}` : 'Unassigned'}
                        </h4>
                        <p className="text-xs text-[#2dd4bf] mb-2">{member.title}</p>
                        
                        {/* Contact Info */}
                        {member.assigned && (
                          <div className="space-y-1 w-full">
                            {member.user.phone && (
                              <a 
                                href={`tel:${member.user.phone}`}
                                className="flex items-center justify-center gap-1 text-xs text-white/60 hover:text-white transition-colors"
                              >
                                <Phone className="h-3 w-3" />
                                <span>{member.user.phone}</span>
                              </a>
                            )}
                            {member.user.email && (
                              <a 
                                href={`mailto:${member.user.email}`}
                                className="flex items-center justify-center gap-1 text-xs text-white/60 hover:text-white transition-colors truncate"
                              >
                                <Mail className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{member.user.email}</span>
                              </a>
                            )}
                          </div>
                        )}
                        
                        {!member.assigned && (
                          <p className="text-xs text-white/40">
                            Contact: {projectTeam.default_contact.phone}
                          </p>
                        )}
                        
                        {/* Message Button */}
                        {member.assigned && (
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => openTeamMessageDialog(member)}
                            className="mt-3 w-full border-white/20 text-white hover:bg-white/10 hover:border-[#2dd4bf]/50"
                            data-testid={`message-${member.role}-btn`}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Message
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Default Contact if no team assigned */}
                {!projectTeam.has_assigned_team && (
                  <div className="mt-4 p-4 bg-[#2dd4bf]/10 rounded-lg border border-[#2dd4bf]/30 text-center">
                    <p className="text-sm text-white/70 mb-2">
                      Your dedicated team is being assigned. In the meantime, contact us:
                    </p>
                    <div className="flex items-center justify-center gap-6">
                      <a href={`tel:${projectTeam.default_contact.phone}`} className="flex items-center gap-2 text-[#2dd4bf] hover:text-[#5eead4]">
                        <Phone className="h-4 w-4" />
                        {projectTeam.default_contact.phone}
                      </a>
                      <a href={`mailto:${projectTeam.default_contact.email}`} className="flex items-center gap-2 text-[#2dd4bf] hover:text-[#5eead4]">
                        <Mail className="h-4 w-4" />
                        {projectTeam.default_contact.email}
                      </a>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-5 gap-4">
              <Card 
                className="cursor-pointer bg-[#0f1219] border-white/10 hover:border-[#2dd4bf]/50 transition-all group" 
                onClick={() => setActiveTab('messages')}
                data-testid="quick-action-messages"
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                    <MessageSquare className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">Message Team</h3>
                    <p className="text-sm text-white/50">Contact your project team</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-white/60 transition-colors" />
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer bg-[#0f1219] border-white/10 hover:border-[#2dd4bf]/50 transition-all group" 
                onClick={() => setActiveTab('colors')}
                data-testid="quick-action-colors"
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                    <Palette className="h-6 w-6 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">Color Selection</h3>
                    <p className="text-sm text-white/50">
                      {dashboard?.project?.color_confirmed ? 'Confirmed' : 'Pending confirmation'}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-white/60 transition-colors" />
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer bg-[#0f1219] border-white/10 hover:border-[#2dd4bf]/50 transition-all group" 
                onClick={() => setShowReferralDialog(true)}
                data-testid="quick-action-referral"
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                    <Gift className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">Refer a Friend</h3>
                    <p className="text-sm text-white/50">Earn rewards</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-white/60 transition-colors" />
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer bg-[#0f1219] border-white/10 hover:border-[#2dd4bf]/50 transition-all group" 
                onClick={() => setShowReviewDialog(true)}
                data-testid="quick-action-review"
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center group-hover:bg-yellow-500/30 transition-colors">
                    <Star className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">Leave a Review</h3>
                    <p className="text-sm text-white/50">
                      {reviews?.has_reviewed ? 'Thank you!' : 'Share your experience'}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-white/60 transition-colors" />
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer bg-[#0f1219] border-white/10 hover:border-[#2dd4bf]/50 transition-all group" 
                onClick={() => setShowPaymentDialog(true)}
                data-testid="quick-action-payment"
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                    <DollarSign className="h-6 w-6 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">Make a Payment</h3>
                    <p className="text-sm text-white/50">Balance: ${(dashboard?.financials?.balance_due || 0).toLocaleString()}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-white/60 transition-colors" />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" data-testid="customer-messages-tab">
            <Card className="bg-[#0f1219] border-white/10 overflow-hidden">
              <div className="flex h-[600px]">
                {/* Left Sidebar - Team Members */}
                <div className="w-80 border-r border-white/10 flex flex-col">
                  {/* Header */}
                  <div className="p-4 border-b border-white/10">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-[#2dd4bf]" />
                      Conversations
                    </h3>
                    <p className="text-xs text-white/50 mt-1">Select a team member to chat</p>
                  </div>
                  
                  {/* Conversation List */}
                  <ScrollArea className="flex-1">
                    {/* All Team - Group Chat */}
                    <div 
                      onClick={() => setActiveConversation('all')}
                      className={`p-4 cursor-pointer border-b border-white/5 transition-all ${
                        activeConversation === 'all' 
                          ? 'bg-[#2dd4bf]/10 border-l-2 border-l-[#2dd4bf]' 
                          : 'hover:bg-white/5'
                      }`}
                      data-testid="conversation-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2dd4bf] to-[#0d9488] flex items-center justify-center">
                          <Users className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-white">Entire Team</p>
                            {getUnreadCount('all') > 0 && (
                              <span className="px-2 py-0.5 bg-[#2dd4bf] text-white text-xs rounded-full">
                                {getUnreadCount('all')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-white/50 truncate">
                            Message all team members at once
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Individual Team Members */}
                    {projectTeam.team.filter(m => m.assigned).map((member) => {
                      const lastMsg = getLastMessage(member.user?.id);
                      return (
                        <div 
                          key={member.role}
                          onClick={() => setActiveConversation(member.user?.id)}
                          className={`p-4 cursor-pointer border-b border-white/5 transition-all ${
                            activeConversation === member.user?.id 
                              ? 'bg-[#2dd4bf]/10 border-l-2 border-l-[#2dd4bf]' 
                              : 'hover:bg-white/5'
                          }`}
                          data-testid={`conversation-${member.role}`}
                        >
                          <div className="flex items-center gap-3">
                            {member.user?.profile_image ? (
                              <img 
                                src={member.user.profile_image} 
                                alt={member.user.first_name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getRoleColor(member.role)} flex items-center justify-center`}>
                                <span className="text-white font-bold">
                                  {getInitials(member.user?.first_name, member.user?.last_name)}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="font-medium text-white">
                                  {member.user?.first_name} {member.user?.last_name}
                                </p>
                                {getUnreadCount(member.user?.id) > 0 && (
                                  <span className="px-2 py-0.5 bg-[#2dd4bf] text-white text-xs rounded-full">
                                    {getUnreadCount(member.user?.id)}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-[#2dd4bf]">{member.title}</p>
                              {lastMsg && (
                                <p className="text-xs text-white/40 truncate mt-1">
                                  {lastMsg.is_from_customer ? 'You: ' : ''}{lastMsg.content}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Show unassigned team members */}
                    {projectTeam.team.filter(m => !m.assigned).map((member) => (
                      <div 
                        key={member.role}
                        className="p-4 border-b border-white/5 opacity-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full bg-white/10 flex items-center justify-center`}>
                            <User className="h-6 w-6 text-white/40" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white/60">{member.title}</p>
                            <p className="text-xs text-white/40">Not yet assigned</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </ScrollArea>
                  
                  {/* Default Contact */}
                  <div className="p-3 border-t border-white/10 bg-white/5">
                    <p className="text-xs text-white/50 mb-1">Need immediate help?</p>
                    <a href={`tel:${projectTeam.default_contact.phone}`} className="text-sm text-[#2dd4bf] hover:text-[#5eead4] flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {projectTeam.default_contact.phone}
                    </a>
                  </div>
                </div>
                
                {/* Right Side - Chat Area */}
                <div className="flex-1 flex flex-col">
                  {/* Chat Header */}
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {activeConversation === 'all' ? (
                        <>
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2dd4bf] to-[#0d9488] flex items-center justify-center">
                            <Users className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-white">Entire Project Team</p>
                            <p className="text-xs text-white/50">
                              {projectTeam.team.filter(m => m.assigned).length} team members
                            </p>
                          </div>
                        </>
                      ) : (
                        (() => {
                          const member = projectTeam.team.find(m => m.user?.id === activeConversation);
                          if (!member) return null;
                          return (
                            <>
                              {member.user?.profile_image ? (
                                <img 
                                  src={member.user.profile_image} 
                                  alt={member.user.first_name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getRoleColor(member.role)} flex items-center justify-center`}>
                                  <span className="text-white font-bold text-sm">
                                    {getInitials(member.user?.first_name, member.user?.last_name)}
                                  </span>
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-white">
                                  {member.user?.first_name} {member.user?.last_name}
                                </p>
                                <p className="text-xs text-[#2dd4bf]">{member.title}</p>
                              </div>
                            </>
                          );
                        })()
                      )}
                    </div>
                    
                    {/* Contact Info */}
                    {activeConversation !== 'all' && (() => {
                      const member = projectTeam.team.find(m => m.user?.id === activeConversation);
                      if (!member?.user) return null;
                      return (
                        <div className="flex items-center gap-3">
                          {member.user.phone && (
                            <a href={`tel:${member.user.phone}`} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                              <Phone className="h-4 w-4 text-white/60" />
                            </a>
                          )}
                          {member.user.email && (
                            <a href={`mailto:${member.user.email}`} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                              <Mail className="h-4 w-4 text-white/60" />
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Messages Area */}
                  <ScrollArea className="flex-1 p-4">
                    {messagesLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="w-8 h-8 border-2 border-[#2dd4bf]/30 border-t-[#2dd4bf] rounded-full animate-spin"></div>
                      </div>
                    ) : getFilteredMessages().length > 0 ? (
                      <div className="space-y-4">
                        {getFilteredMessages().map((msg, idx) => (
                          <div 
                            key={msg.id || idx} 
                            className={`flex ${msg.is_from_customer ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[70%] ${msg.is_from_customer ? 'order-2' : 'order-1'}`}>
                              {!msg.is_from_customer && (
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="w-6 h-6 rounded-full bg-[#2dd4bf]/20 flex items-center justify-center">
                                    <User className="h-3 w-3 text-[#2dd4bf]" />
                                  </div>
                                  <span className="text-xs text-white/50">{msg.sender_name || 'Rhino Shield Team'}</span>
                                </div>
                              )}
                              <div className={`p-3 rounded-2xl ${
                                msg.is_from_customer 
                                  ? 'bg-[#2dd4bf] text-white rounded-br-md' 
                                  : 'bg-white/10 text-white rounded-bl-md'
                              }`}>
                                <p className="text-sm">{msg.content}</p>
                              </div>
                              <p className={`text-xs text-white/40 mt-1 ${msg.is_from_customer ? 'text-right' : 'text-left'}`}>
                                {new Date(msg.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
                          <MessageSquare className="h-10 w-10 text-white/20" />
                        </div>
                        <p className="text-white/60 font-medium text-lg">No messages yet</p>
                        <p className="text-sm text-white/40 mt-1 max-w-xs">
                          {activeConversation === 'all' 
                            ? 'Send a message to start a conversation with your entire project team'
                            : `Start a conversation with ${projectTeam.team.find(m => m.user?.id === activeConversation)?.user?.first_name || 'this team member'}`
                          }
                        </p>
                      </div>
                    )}
                  </ScrollArea>
                  
                  {/* Message Input */}
                  <div className="p-4 border-t border-white/10 bg-[#0a0d14]">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={activeConversation === 'all' 
                            ? "Message entire team..." 
                            : `Message ${projectTeam.team.find(m => m.user?.id === activeConversation)?.user?.first_name || 'team member'}...`
                          }
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          data-testid="message-input"
                        />
                      </div>
                      <Button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sendingMessage}
                        className="bg-[#2dd4bf] hover:bg-[#14b8a6] text-white px-6"
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Send
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="bg-[#0f1219] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Your Documents</CardTitle>
                <CardDescription className="text-white/50">Contracts, invoices, and project documents</CardDescription>
              </CardHeader>
              <CardContent>
                {documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#2dd4bf]/20 rounded-lg flex items-center justify-center">
                            <FileText className="h-5 w-5 text-[#2dd4bf]" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{doc.title}</p>
                            <p className="text-sm text-white/50">{doc.document_type} - {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" asChild className="border-white/20 text-white hover:bg-white/10">
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-white/20" />
                    <p className="text-white/60">No documents available yet</p>
                    <p className="text-sm text-white/40">Documents will appear here once uploaded by your project team</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Colors Tab */}
          <TabsContent value="colors">
            <Card className="bg-[#0f1219] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Color Selection</CardTitle>
                <CardDescription className="text-white/50">Confirm your coating colors for the project</CardDescription>
              </CardHeader>
              <CardContent>
                {colorSelection?.confirmed ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 p-4 bg-[#2dd4bf]/10 rounded-lg border border-[#2dd4bf]/30">
                      <CheckCircle className="h-6 w-6 text-[#2dd4bf]" />
                      <div>
                        <p className="font-semibold text-[#2dd4bf]">Colors Confirmed</p>
                        <p className="text-sm text-[#2dd4bf]/70">Your color selection has been confirmed and locked in.</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-sm text-white/50 mb-2">Primary Color</p>
                        <p className="font-semibold text-white">{colorSelection.color_selection?.primary_color || 'Not set'}</p>
                      </div>
                      <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-sm text-white/50 mb-2">Accent Color</p>
                        <p className="font-semibold text-white">{colorSelection.color_selection?.accent_color || 'Not set'}</p>
                      </div>
                      <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-sm text-white/50 mb-2">Trim Color</p>
                        <p className="font-semibold text-white">{colorSelection.color_selection?.trim_color || 'Not set'}</p>
                      </div>
                    </div>
                    
                    {colorSelection.color_selection?.notes && (
                      <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-sm text-white/50 mb-2">Notes</p>
                        <p className="text-white">{colorSelection.color_selection.notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Palette className="h-12 w-12 mx-auto mb-4 text-purple-400" />
                    <h3 className="text-lg font-semibold mb-2 text-white">Confirm Your Colors</h3>
                    <p className="text-white/50 mb-6 max-w-md mx-auto">
                      Please confirm your color selections before we begin production. 
                      Once confirmed, colors cannot be changed.
                    </p>
                    <Button 
                      onClick={() => setShowColorDialog(true)}
                      className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488] hover:from-[#14b8a6] hover:to-[#0f766e]"
                    >
                      <Palette className="h-4 w-4 mr-2" />
                      Select & Confirm Colors
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <Card className="bg-[#0f1219] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Your Referrals</CardTitle>
                  <CardDescription className="text-white/50">Refer friends and family to earn rewards</CardDescription>
                </div>
                <Button 
                  onClick={() => setShowReferralDialog(true)}
                  className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488] hover:from-[#14b8a6] hover:to-[#0f766e]"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit Referral
                </Button>
              </CardHeader>
              <CardContent>
                {referrals.length > 0 ? (
                  <div className="space-y-3">
                    {referrals.map(ref => (
                      <div key={ref.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-purple-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{ref.referee_name}</p>
                            <p className="text-sm text-white/50">{ref.referee_phone}</p>
                          </div>
                        </div>
                        <Badge 
                          variant={ref.status === 'rewarded' ? 'default' : 'outline'}
                          className={
                            ref.status === 'rewarded' ? 'bg-[#2dd4bf] text-white' :
                            ref.status === 'sold' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                            'bg-white/10 text-white/60 border-white/20'
                          }
                        >
                          {ref.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Gift className="h-12 w-12 mx-auto mb-4 text-white/20" />
                    <p className="font-medium text-white/60">No referrals yet</p>
                    <p className="text-sm mb-4 text-white/40">Know someone who needs Rhino Shield? Refer them and earn rewards!</p>
                    <Button 
                      onClick={() => setShowReferralDialog(true)}
                      className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488] hover:from-[#14b8a6] hover:to-[#0f766e]"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Submit Your First Referral
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <Card className="bg-gradient-to-br from-green-600 to-green-700 border-0">
                <CardContent className="p-6">
                  <p className="text-green-100 text-sm">Total Paid</p>
                  <p className="text-3xl font-bold text-white">${(payments?.total_paid || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-600 to-amber-700 border-0">
                <CardContent className="p-6">
                  <p className="text-amber-100 text-sm">Balance Due</p>
                  <p className="text-3xl font-bold text-white">${(payments?.balance_due || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer bg-[#0f1219] border-white/10 hover:border-[#2dd4bf]/50 transition-all" 
                onClick={() => setShowPaymentDialog(true)}
              >
                <CardContent className="p-6 flex items-center justify-center h-full">
                  <div className="text-center">
                    <CreditCard className="h-8 w-8 mx-auto mb-2 text-[#2dd4bf]" />
                    <p className="font-semibold text-white">Make a Payment</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-[#0f1219] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Payment History</CardTitle>
              </CardHeader>
              <CardContent>
                {payments?.payments?.length > 0 ? (
                  <div className="space-y-3">
                    {payments.payments.map(payment => (
                      <div key={payment.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            payment.status === 'completed' ? 'bg-green-500/20' : 'bg-white/10'
                          }`}>
                            <DollarSign className={`h-5 w-5 ${
                              payment.status === 'completed' ? 'text-green-400' : 'text-white/40'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-white">${payment.amount.toLocaleString()}</p>
                            <p className="text-sm text-white/50">
                              {new Date(payment.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge 
                          variant={payment.status === 'completed' ? 'default' : 'outline'}
                          className={payment.status === 'completed' ? 'bg-green-500' : 'border-white/20 text-white/60'}
                        >
                          {payment.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CreditCard className="h-12 w-12 mx-auto mb-4 text-white/20" />
                    <p className="text-white/60">No payments recorded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Color Selection Dialog */}
      <Dialog open={showColorDialog} onOpenChange={setShowColorDialog}>
        <DialogContent className="bg-[#0f1219] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Confirm Your Colors</DialogTitle>
            <DialogDescription className="text-white/50">
              Please enter your color selections. Once confirmed, colors cannot be changed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-white/80">Primary Color *</label>
              <Input
                value={colorForm.primary_color}
                onChange={(e) => setColorForm({ ...colorForm, primary_color: e.target.value })}
                placeholder="e.g., Desert Sand, Forest Green"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Accent Color</label>
              <Input
                value={colorForm.accent_color}
                onChange={(e) => setColorForm({ ...colorForm, accent_color: e.target.value })}
                placeholder="Optional"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Trim Color</label>
              <Input
                value={colorForm.trim_color}
                onChange={(e) => setColorForm({ ...colorForm, trim_color: e.target.value })}
                placeholder="Optional"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Notes</label>
              <Textarea
                value={colorForm.notes}
                onChange={(e) => setColorForm({ ...colorForm, notes: e.target.value })}
                placeholder="Any special instructions or notes"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowColorDialog(false)} className="border-white/20 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button 
              onClick={handleColorSubmit} 
              disabled={!colorForm.primary_color}
              className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488]"
            >
              Confirm Colors
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Referral Dialog */}
      <Dialog open={showReferralDialog} onOpenChange={setShowReferralDialog}>
        <DialogContent className="bg-[#0f1219] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Submit a Referral</DialogTitle>
            <DialogDescription className="text-white/50">
              Refer a friend or family member and earn rewards when they become a customer!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-white/80">Their Name *</label>
              <Input
                value={referralForm.referee_name}
                onChange={(e) => setReferralForm({ ...referralForm, referee_name: e.target.value })}
                placeholder="Full name"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Phone Number *</label>
              <Input
                value={referralForm.referee_phone}
                onChange={(e) => setReferralForm({ ...referralForm, referee_phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Email</label>
              <Input
                type="email"
                value={referralForm.referee_email}
                onChange={(e) => setReferralForm({ ...referralForm, referee_email: e.target.value })}
                placeholder="Optional"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Address</label>
              <Input
                value={referralForm.referee_address}
                onChange={(e) => setReferralForm({ ...referralForm, referee_address: e.target.value })}
                placeholder="Optional - helps us serve them better"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Notes</label>
              <Textarea
                value={referralForm.notes}
                onChange={(e) => setReferralForm({ ...referralForm, notes: e.target.value })}
                placeholder="Best time to call, how they heard about Rhino Shield, etc."
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                rows={2}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowReferralDialog(false)} className="border-white/20 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button 
              onClick={handleReferralSubmit}
              className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488]"
            >
              <Send className="h-4 w-4 mr-2" />
              Submit Referral
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-[#0f1219] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Make a Payment</DialogTitle>
            <DialogDescription className="text-white/50">
              Balance due: ${(payments?.balance_due || 0).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-white/80">Payment Amount *</label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            
            <div className="p-4 bg-[#2dd4bf]/10 rounded-lg border border-[#2dd4bf]/30">
              <p className="text-sm text-[#2dd4bf]">
                <strong>Secure Payment:</strong> Your payment will be processed securely through Stripe.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)} className="border-white/20 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button 
              onClick={handlePayment}
              className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488]"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Continue to Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Multi-Platform Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={resetReviewDialog}>
        <DialogContent className="bg-[#0f1219] border-white/10 text-white max-w-xl" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-400" />
              {reviewStep === 1 && 'Write Your Review'}
              {reviewStep === 2 && 'Select Platforms'}
              {reviewStep === 3 && 'Share Your Review'}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              {reviewStep === 1 && 'Share your experience with Rhino Shield! Your feedback helps us improve.'}
              {reviewStep === 2 && 'Choose where you\'d like to share your review.'}
              {reviewStep === 3 && 'Post your review to the platforms below. Click each button to open the platform.'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Step 1: Write Review */}
          {reviewStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <label className="text-sm font-medium block mb-3 text-white/80">How would you rate your experience?</label>
                <div className="flex justify-center">
                  <StarRating 
                    rating={reviewForm.rating} 
                    onRatingChange={(rating) => setReviewForm({ ...reviewForm, rating })} 
                  />
                </div>
                <p className="text-sm text-white/50 mt-2">
                  {reviewForm.rating === 5 ? 'Excellent!' : 
                   reviewForm.rating === 4 ? 'Great!' : 
                   reviewForm.rating === 3 ? 'Good' : 
                   reviewForm.rating === 2 ? 'Fair' : 'Poor'}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-white/80">Review Title (Optional)</label>
                <Input
                  value={reviewForm.title}
                  onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                  placeholder="Summarize your experience..."
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="review-title-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-white/80">Your Review *</label>
                <Textarea
                  value={reviewForm.review_text}
                  onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                  placeholder="Tell us about your experience with Rhino Shield. What did you love? How was the process?"
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  rows={4}
                  data-testid="review-text-input"
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="would-recommend"
                    checked={reviewForm.would_recommend}
                    onCheckedChange={(checked) => setReviewForm({ ...reviewForm, would_recommend: checked })}
                    className="border-white/30 data-[state=checked]:bg-[#2dd4bf] data-[state=checked]:border-[#2dd4bf]"
                  />
                  <label htmlFor="would-recommend" className="text-sm text-white/80">
                    I would recommend Rhino Shield to friends and family
                  </label>
                </div>
                
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="allow-public"
                    checked={reviewForm.allow_public}
                    onCheckedChange={(checked) => setReviewForm({ ...reviewForm, allow_public: checked })}
                    className="border-white/30 data-[state=checked]:bg-[#2dd4bf] data-[state=checked]:border-[#2dd4bf]"
                  />
                  <label htmlFor="allow-public" className="text-sm text-white/80">
                    You may use my review as a testimonial on your website
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={resetReviewDialog}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => setReviewStep(2)} 
                  disabled={!reviewForm.review_text.trim()}
                  className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488]"
                  data-testid="review-next-btn"
                >
                  Next: Choose Platforms
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Select Platforms */}
          {reviewStep === 2 && (
            <div className="space-y-6">
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-medium text-white mb-2">Your Review Preview</h4>
                <div className="flex items-center gap-1 mb-2">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className={`h-4 w-4 ${s <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-white/20'}`} />
                  ))}
                </div>
                {reviewForm.title && <p className="font-medium text-white/80 mb-1">{reviewForm.title}</p>}
                <p className="text-sm text-white/60 line-clamp-3">{reviewForm.review_text}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-white/80 block mb-3">
                  Select platforms to share your review:
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {REVIEW_PLATFORMS.map(platform => (
                    <div
                      key={platform.id}
                      onClick={() => togglePlatform(platform.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        reviewForm.platforms.includes(platform.id)
                          ? 'bg-[#2dd4bf]/10 border-[#2dd4bf]/50'
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`platform-${platform.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{platform.icon}</span>
                        <div className="flex-1">
                          <p className="font-medium text-white">{platform.name}</p>
                          <p className="text-xs text-white/50">{platform.description}</p>
                        </div>
                        {reviewForm.platforms.includes(platform.id) && (
                          <CheckCircle className="h-5 w-5 text-[#2dd4bf]" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-between gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setReviewStep(1)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleReviewSubmit}
                  disabled={reviewForm.platforms.length === 0}
                  className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488]"
                  data-testid="review-submit-btn"
                >
                  Save & Continue
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Post to Platforms */}
          {reviewStep === 3 && (
            <div className="space-y-6">
              <div className="p-4 bg-[#2dd4bf]/10 rounded-lg border border-[#2dd4bf]/30">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-[#2dd4bf]" />
                  <p className="font-medium text-[#2dd4bf]">Review Saved!</p>
                </div>
                <p className="text-sm text-white/60">
                  Now share it on other platforms to help more homeowners discover Rhino Shield.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-white/80">
                    Click each platform to post your review:
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyReviewToClipboard}
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="copy-review-btn"
                  >
                    {copiedToClipboard ? (
                      <>
                        <Check className="h-4 w-4 mr-1 text-[#2dd4bf]" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy Review
                      </>
                    )}
                  </Button>
                </div>

                {REVIEW_PLATFORMS.filter(p => reviewForm.platforms.includes(p.id)).map(platform => (
                  <Button
                    key={platform.id}
                    onClick={() => openPlatformForReview(platform)}
                    className={`w-full justify-between ${platform.color} hover:opacity-90`}
                    data-testid={`post-to-${platform.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <span>{platform.icon}</span>
                      Post to {platform.name}
                    </span>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                ))}
              </div>

              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-white/40 text-center mb-4">
                  Your review has been copied to clipboard. Simply paste it when each platform opens.
                </p>
                <Button 
                  onClick={resetReviewDialog}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                  data-testid="review-done-btn"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Message Dialog */}
      <Dialog open={showTeamMessageDialog} onOpenChange={setShowTeamMessageDialog}>
        <DialogContent className="bg-[#0f1219] border-white/10 text-white" data-testid="team-message-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-[#2dd4bf]" />
              {selectedTeamMember ? `Message ${selectedTeamMember.user?.first_name}` : 'Message Entire Team'}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              {selectedTeamMember 
                ? `Send a direct message to ${selectedTeamMember.user?.first_name} ${selectedTeamMember.user?.last_name} (${selectedTeamMember.title})`
                : 'Your message will be sent to all team members assigned to your project'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Recipient Info */}
            {selectedTeamMember?.user && (
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                {selectedTeamMember.user.profile_image ? (
                  <img 
                    src={selectedTeamMember.user.profile_image} 
                    alt={`${selectedTeamMember.user.first_name}`}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getRoleColor(selectedTeamMember.role)} flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">
                      {getInitials(selectedTeamMember.user.first_name, selectedTeamMember.user.last_name)}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-medium text-white">
                    {selectedTeamMember.user.first_name} {selectedTeamMember.user.last_name}
                  </p>
                  <p className="text-xs text-[#2dd4bf]">{selectedTeamMember.title}</p>
                </div>
              </div>
            )}
            
            {!selectedTeamMember && (
              <div className="flex items-center gap-3 p-3 bg-[#2dd4bf]/10 rounded-lg border border-[#2dd4bf]/30">
                <div className="w-10 h-10 rounded-full bg-[#2dd4bf]/20 flex items-center justify-center">
                  <Users className="h-5 w-5 text-[#2dd4bf]" />
                </div>
                <div>
                  <p className="font-medium text-white">Entire Project Team</p>
                  <p className="text-xs text-white/50">Sales Rep, Project Manager, Operations, Support</p>
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-white/80">Your Message</label>
              <Textarea
                value={teamMessage}
                onChange={(e) => setTeamMessage(e.target.value)}
                placeholder="Type your message here..."
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                rows={4}
                data-testid="team-message-input"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowTeamMessageDialog(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleTeamMessage}
              disabled={!teamMessage.trim() || sendingMessage}
              className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488]"
              data-testid="send-team-message-btn"
            >
              {sendingMessage ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </div>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
